import { CommonModule } from '@angular/common';
import { booleanAttribute, Component, computed, input, output, ChangeDetectionStrategy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';

export type AlertTypes = 'tip' | 'info' | 'warning' | 'success' | 'error' | 'important';

export type AlertSizes = 'large' | 'small';

export type AlertActionButtonTypes = 'filled' | 'outlined';

const alertTypeIcons: Record<AlertTypes, string> = {
  tip: 'info_outline',
  info: 'info',
  warning: 'warning',
  success: 'check',
  error: 'error',
  important: 'info_outline',
};

@Component({
  selector: 'glxy-alert',
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.scss', './alert-type-colors.scss'],
  imports: [CommonModule, MatIconModule, RouterModule, MatButtonModule],
  // TODO: Remove directive host when Angular adds support for direct host binding against signals.
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.has-border]': 'border()',
    '[class.has-border-radius]': 'borderRadius()',
    '[class.extended-has-border]': 'extendedBorder()',
    '[class]': 'alertClass()',
  },
})
export class AlertComponent {
  // Choose the alert appearance and icon (default: info)
  readonly type = input<AlertTypes>('info');

  // solid color alert appearance for more impact (default: false)
  readonly solidColor = input(false, { transform: booleanAttribute });

  // use a custom mat-icon (optional)
  readonly icon = input<string | undefined>(undefined);

  // use a custom svg icon (optional)
  readonly svgIcon = input<string | undefined>(undefined);

  // select the size of the alert (default: large)
  readonly size = input<AlertSizes>('large');

  // show or hide close button (default: false)
  readonly showClose = input(false, { transform: booleanAttribute });

  // show or hide icon (default: true)
  readonly showIcon = input(true, { transform: booleanAttribute });

  // custom close button text (default: Dismiss)
  readonly closeTitle = input('Dismiss');

  // show or hide action button (default: false)
  readonly showAction = input(false, { transform: booleanAttribute });

  // action button title text (default: Action Button)
  readonly actionTitle = input('Action Button');

  // router link to navigate to when button clicked
  readonly actionRouterLink = input<string | undefined>(undefined);

  // external url to open in new tab when button clicked
  readonly actionExternalLink = input<string | undefined>(undefined);

  // emphasis of the action button (default: outlined)
  readonly actionButtonType = input<AlertActionButtonTypes>('outlined');

  // show or hide mobile view (default: false)
  readonly stackedView = input(false, { transform: booleanAttribute });

  // Hide or show the border on regular alert appearance (default: true)
  readonly border = input(true, { transform: booleanAttribute });

  // Enable or disable the border-radius on the alert (default: true)
  readonly borderRadius = input(true, { transform: booleanAttribute });

  // Hide or show the border on regular alert appearance (default: true)
  readonly extendedBorder = input(true, { transform: booleanAttribute });

  // event emitted when clicking the close button
  // eslint-disable-next-line @angular-eslint/no-output-native
  readonly close = output<void>();

  // event emmited when action button clicked
  readonly actionClick = output<void>();

  // add class for alert type and appearance
  protected alertClass = computed(
    () => `glxy-alert alert-${this.type()}${this.solidColor() ? '-solid' : ''} alert-size-${this.size()}`,
  );

  // if user specifies custom mat-icon, use that.
  // If no custom icon, show icons that matches alert type.
  protected get currentIcon(): string | null {
    if (this.svgIcon()) {
      return null;
    }

    return this.icon() ?? alertTypeIcons[this.type()];
  }
}
